import { Injectable } from '@angular/core';
import { MistService } from '@app/features/mists/shared/mist.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Mist } from '@app/shared/models/mist/mist.model';
import { MistLocation } from '@app/shared/models/mist/mist-location';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ChainWideMistsService {
    constructor(
        private mistsService: MistService,
        private locationsService: LocationsService
    ) { }

    async getAllMists(): Promise<Mist[]> {
        const allMists: Mist[] = [];
        try {
            const observable = await this.mistsService.getAllMistsLatent();
            const mists_from_response = await firstValueFrom(observable);
            if (typeof mists_from_response === 'string') {
                console.log("Mists timed out!")
                return allMists;
            }
            mists_from_response["message"].forEach(mist_json => {
                let mist: Mist = mist_json;
                allMists.push(mist);
            });
        } catch (error) {
            console.log("Mists timed out!")
        }
        return allMists;
    }

    async getAllLocations(): Promise<Location[]> {
        const locations: Location[] = [];
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return locations;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
        return locations;
    }

    private sortMists(allMists: Mist[]): Map<string, Mist[]> {
        let sortedMists = new Map<string, Mist[]>();
        for (let mist of allMists) {
            let storeNumber = "";

            if (mist.name != null) {
                storeNumber = mist.name.substring(0, 5);
            }

            if (sortedMists.has(storeNumber)) {
                sortedMists.get(storeNumber).push(mist);
            } else {
                let mists = [];
                mists.push(mist);
                sortedMists.set(storeNumber, mists);
            }
        }
        return sortedMists;
    }

    private sortLocations(locations: Location[]): Map<string, Location> {
        let sortedLocations = new Map<string, Location>();
        locations.forEach(location => {
            sortedLocations.set(location.number, location);
        });
        return sortedLocations;
    }

    async createDataSource(locations?: Location[]): Promise<MistLocation[]> {
        const mistLocations: MistLocation[] = [];
        let locationsToUse: Location[];

        if (locations == null) {
            locationsToUse = await this.getAllLocations();
        } else {
            locationsToUse = locations;
        }

        const allMists = await this.getAllMists();
        const sortedMists = this.sortMists(allMists);
        const sortedLocation = this.sortLocations(locationsToUse);

        sortedMists.forEach((value: Mist[], key: string) => {
            if (sortedLocation.has(key)) {
                let mistLocation: MistLocation = new MistLocation;
                mistLocation.mists = value;
                mistLocation.location = sortedLocation.get(key);
                mistLocations.push(mistLocation);
            }
        });

        return mistLocations;
    }
}
